import React, { useRef, Fragment, useContext } from 'react';
import classNames from 'classnames';
import { NavLink } from 'react-router-dom';
import { Image } from 'api/interfaces';

import { createUseStyles } from 'react-jss';

import styles from './PodcastTile.styles';

import * as analytics from 'analytics';
import { getImageWithResamplingQuery } from 'util/resampling';
import { scrollY } from 'util/noscroll';
import { isMobile, isTouch } from 'util/device';
import ChevronIcon from 'components/svg/Chevron';

import TileLabel from 'components/TileLabel/index';
import CustomLink from 'components/CustomLink';
import Favourite from 'components/svg/Favourite';
import LazyTileImage from 'components/LazyTileImage';
import CollectionContext from 'components/Collection/CollectionContext';
import { Podcast } from 'api/models';

import Marquee from 'components/Marquee';

function navLink(pathname: string, podcastName: string, userSearched: string) {
  return {
    pathname,
    state: {
      analytics: analytics.generateTileClick(podcastName, pathname, 'podcast page', userSearched),
    },
  };
}

export interface PodcastTileProps {
  id?: string;
  author?: string;
  slug?: string;
  collectionId?: string;
  display?: string;
  error?: boolean;
  foldIsOpen?: boolean;
  hideText?: boolean;
  image?: Image;
  isFavorite?: string | boolean;
  isInModal?: boolean;
  isInSearch?: boolean;
  podcastSlugInFold?: string;
  contentTypeId?: string;
  title?: string;
  userSearched?: string;
  onFavoriteToggle: (podcast: Podcast) => void;
  openFold: (slug: string, collectionId: string) => void;
  closeFold: () => void;
  saveRecentSearch?: (contentTypeId: string, slug: string) => void;
}

const useStyles = createUseStyles(styles, { name: 'PodcastTile' });

const PodcastTile = ({
  author,
  error,
  hideText,
  image,
  isFavorite,
  slug,
  title,
  userSearched,
  saveRecentSearch,
  contentTypeId,
  id,
  collectionId,
  onFavoriteToggle,
  closeFold,
  foldIsOpen,
  podcastSlugInFold,
  openFold,
}: PodcastTileProps): JSX.Element => {
  const classes = useStyles();
  const tileRef = useRef(null);

  const { isInSearch, withFold } = useContext(CollectionContext);

  const scrollToTile = () => {
    const { top, height } = tileRef.current.getBoundingClientRect();
    scrollY(top - height / 2);
  };

  const handleClick = () => {
    if (withFold) {
      if (foldIsOpen && podcastSlugInFold === slug) {
        closeFold();
      } else {
        openFold(slug, collectionId);
        setTimeout(scrollToTile, 200);
      }
    }
  };

  const handleFavoriteClick = () => {
    if (isTouch) {
      handleClick();
      return;
    }
    onFavoriteToggle({ id, slug, contentTypeId, title } as Podcast);
  };

  const saveToLocalStorage = () => {
    saveRecentSearch(contentTypeId, slug);
  };

  return (
    <div>
      <div data-testid={`podcast_${slug}`} ref={tileRef} className={classes.podcastTile}>
        <TileLabel slug={slug} />
        <LazyTileImage
          data-key="tile-image"
          className={classNames(classes.backdropDefault, classes.backdropDefaultFix)}
          imageUrl={getImageWithResamplingQuery({ url: image?.url })}
        />

        <div
          onClick={isInSearch ? saveToLocalStorage : undefined}
          className={classNames(classes.hoverOverlayDefault, {
            [classes.noHover]: isTouch,
          })}
          data-testid="podcastTile_overlay"
          role="presentation"
        >
          <CustomLink
            isLink={!withFold && !error}
            handleClick={error ? handleFavoriteClick : handleClick}
            className={classes.pageLinkDefault}
            to={!withFold ? navLink(`/podcasts/${slug}`, title, userSearched) : null}
            data-testid="podcast_tile_more_open_podcast"
            aria-label={`Ga naar ${title}`}
          />
          <button
            className={classes.addToFavoritesButton}
            type="button"
            data-testid="podcastTile_add"
            onClick={handleFavoriteClick}
          >
            <Favourite isActive={isFavorite} />
          </button>
          <NavLink className={classes.link} to={`/podcasts/${slug}`}>
            <span>Ga naar podcast</span>
            <ChevronIcon className={classes.chevron} />
          </NavLink>
          <div className={classes.hoverBackdropDefault} />
        </div>
      </div>
      {!hideText && !isMobile && (
        <Fragment>
          <h3 className={classes.title}>
            <NavLink className={classes.titleLink} to={`/podcasts/${slug}`} title={title}>
              {title}
            </NavLink>
            {error && 'Podcast helaas niet gevonden'}
            <Marquee text={author} />
          </h3>
          <span className={classes.screenReaderTitleDefault}>
            {title} {author}
          </span>
        </Fragment>
      )}
    </div>
  );
};

export default PodcastTile;
